import { useMemo } from 'react';
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form';
import { adaptQuestions, type BackendQuestion, type BidFormField } from '@tempmee/awards';
import {
  Button,
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useSubmitBidFeedback } from '~/domain/BestInDental';
import {
  BidFormFieldRenderer,
  BidSubmitError,
  BidTermsCheckbox,
  buildBidAnswers,
  buildBidDefaultValues,
  buildBidFormResolver,
  validateBidTermsField,
} from '../shared';
import styles from './BidFeedbackDialog.module.css';

const SURVEY_DESCRIPTION =
  'This professional or office has been nominated for Best in Dental. Your honest feedback helps GoTu recognize the offices and professionals who set the standard for quality, reliability, and care.';

const DEFAULT_APPLICANT_LABEL = 'this professional';

const withApplicantName = (fields: BidFormField[], applicantName: string | null): BidFormField[] =>
  fields.map((field) => ({
    ...field,
    label: field.label.replaceAll('{NAME}', applicantName ?? DEFAULT_APPLICANT_LABEL),
  }));

// awardTermsAccepted is a top-level submit field, not a question — validate it
// so feedback can never attest to terms the user never checked (the server
// 400s on it, and attesting unchecked consent is a compliance bug).
const validateFeedbackExtras = (
  values: Record<string, unknown>,
): FieldErrors<Record<string, unknown>> => validateBidTermsField('awardTermsAccepted', values);

interface IBidFeedbackDialogProps {
  cycleId: string;
  /** The counterparty being reviewed (the professional who worked the shift). */
  applicantUserId: string;
  /** Display name for `{NAME}` substitution — the gate response omits it. */
  applicantName: string | null;
  questions: BackendQuestion[];
  /** Called on every dismissal path (close, secondary action) AND, unless `onSubmitted` is given, on submit success. */
  onComplete: () => void;
  /** Submit-success handler; falls back to `onComplete` (the EOD chain treats both the same). */
  onSubmitted?: () => void;
  /** Optional submit provenance — the EOD chain has the rated shift's job; the pending tab has none. */
  jobId?: string;
  /** Submits (and asks) as this office via `x-office` — required from pending-list rows. */
  officeId?: string;
  /** Secondary-action label: "Skip" mid-EOD-chain, "Cancel" from the pending tab. */
  dismissLabel?: string;
  dataCy?: string;
}

/**
 * The CS-5 Best-in-Dental feedback dialog, shared by the EOD rating chain
 * (`BidEodFeedbackStep`) and the pending-feedback tab. Callers own the gate:
 * render this only with a resolved (cycle, applicant) pair and its questions.
 */
export const BidFeedbackDialog = ({
  cycleId,
  applicantUserId,
  applicantName,
  questions,
  onComplete,
  onSubmitted,
  jobId,
  officeId,
  dismissLabel = 'Skip',
  dataCy = 'bid-eod-feedback-step',
}: IBidFeedbackDialogProps) => {
  const mutation = useSubmitBidFeedback();
  const adaptedFields = useMemo(() => adaptQuestions(questions), [questions]);
  const fields = useMemo(
    () => withApplicantName(adaptedFields, applicantName),
    [adaptedFields, applicantName],
  );
  const resolver = useMemo(
    () => buildBidFormResolver('office-reviews-pro-feedback', adaptedFields, validateFeedbackExtras),
    [adaptedFields],
  );

  const form = useForm({
    resolver,
    defaultValues: buildBidDefaultValues(adaptedFields, { awardTermsAccepted: '' }),
  });

  const onSubmit = (values: Record<string, unknown>) => {
    mutation.mutate(
      {
        cycleId,
        applicantUserId,
        ...(jobId ? { jobId } : {}),
        ...(officeId ? { officeId } : {}),
        answers: buildBidAnswers(values, ['awardTermsAccepted']),
        awardTermsAccepted: values.awardTermsAccepted === 'true',
      },
      { onSuccess: onSubmitted ?? onComplete },
    );
  };

  const handleRetry = () => mutation.reset();

  return (
    <Dialog
      open
      onClose={onComplete}
      // A stray click outside must not throw away a half-filled survey.
      closeOnOverlayClick={false}
      size="large"
      data-cy={dataCy}
    >
      <DialogTitle onClose={onComplete}>Best in Dental Feedback</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogContent className={styles.dialogContent}>
            <Column gap={4}>
              <Column gap={2}>
                <Divider />
                <Typography color={colors.text.secondary}>{SURVEY_DESCRIPTION}</Typography>
              </Column>

              {fields.map((field) => (
                <BidFormFieldRenderer key={field.name} field={field} />
              ))}

              <BidTermsCheckbox name="awardTermsAccepted" dataCy="bid-eod-feedback-terms" />

              {mutation.isError && (
                <BidSubmitError
                  data-cy="bid-eod-feedback-error"
                  message="Something went wrong submitting your feedback. Please try again."
                  onRetry={handleRetry}
                />
              )}
            </Column>
          </DialogContent>

          <DialogActions>
            <Button
              type="button"
              variant="outlined"
              onClick={onComplete}
              data-cy="bid-eod-feedback-skip"
            >
              {dismissLabel}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || mutation.isError}
              data-cy="bid-eod-feedback-submit"
            >
              Submit
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
